/**
 * InstrumentModal.tsx
 * Universal modal for both adding new instruments and editing existing ones
 * 
 * Features:
 * - Dual mode: Add new instrument vs Edit existing instrument
 * - Form pre-population for edit mode
 * - HeroUI form components with validation
 * - Conditional titles and buttons based on mode
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input, Select, SelectItem, NumberInput, TextArea, Accordion, AccordionItem, Switch, Chip, useDisclosure } from '@enfomentis/ui';
import { CurrencyChooserModal } from '@/components/shared/CurrencyChooser';
import type { Instrument } from '@/types/schema/Instrument';
import type { AssetClassType, MarketSegment, SubAssetClass } from '@/types/shared/AssetClass';
import { VALID_MARKET_SEGMENTS_BY_ASSET_CLASS, VALID_SUB_ASSET_CLASSES_BY_ASSET_CLASS } from '@/constants/assetClass';
import type { IncrementType } from '@/types/shared/IncrementType';
import type { EntityStatus } from '@/types/shared/EntityStatus';
import { getCurrencyDisplayName, getCurrencySymbol } from '@/utils';
import { CURRENCY_DATABASE } from '@/constants';
import { instrumentService } from '@/services/InstrumentService';
import { getInstrumentById } from '@/data/instrumentCatalog';
import type { InstrumentCatalogEntry } from '@/types/reference/InstrumentCatalog';

/**
 * Form data interface for the modal
 */
interface InstrumentFormData {
  symbol: string;
  displayName: string;
  assetClass: AssetClassType;
  marketSegment: MarketSegment;
  subAssetClass: SubAssetClass | '';
  customMarketSegment: string;
  customSubAssetClass: string;
  contractSize: number;
  contractAsset: string;
  incrementSize: number;
  incrementType: IncrementType;
  quoteCurrency: string;
  notes: string;
  status: EntityStatus;
}

/**
 * Initial context for pre-populating form fields when creating new instruments
 */
export interface InstrumentInitialContext {
  assetClass?: AssetClassType;
  marketSegment?: MarketSegment;
  subAssetClass?: SubAssetClass;
}

/**
 * Modal mode type for explicit control
 */
export type InstrumentModalMode = 'add' | 'edit' | 'view';

/**
 * Props for the InstrumentModal component
 */
interface InstrumentModalProps {
  /** Whether the modal is open */
  isOpen: boolean;

  /** Handler for closing the modal */
  onClose: () => void;

  /** Optional instrument to edit (if provided, modal is in edit mode) */
  instrument?: Instrument;

  /** Optional instrument to duplicate from (pre-fills form for new instrument creation) */
  duplicateFrom?: Instrument;

  /** Explicit mode control - if not specified, derived from instrument prop */
  mode?: InstrumentModalMode;

  /** Optional initial context for pre-populating form when creating new instruments */
  initialContext?: InstrumentInitialContext;

  /** When true, disables asset class, market segment, and sub-asset class selects (used when classification is determined by parent context) */
  classificationLocked?: boolean;

  /** Handler for successful save (both add and edit) */
  onSave: (instrument: Instrument) => void;

  /** Handler for save errors */
  onError?: (error: string) => void;
}

/**
 * Asset class options for the select dropdown
 */
const assetClassOptions: AssetClassType[] = ['futures', 'cfd', 'forex', 'options', 'stocks', 'bonds', 'crypto', 'funds'];

/**
 * Increment type options for the select dropdown  
 */
const incrementTypeOptions = ['tick', 'pip', 'pipette', 'point', 'cent', 'dollar', 'increment'] as const;



/**
 * InstrumentModal component for adding and editing instruments
 */
export const InstrumentModal: React.FC<InstrumentModalProps> = ({
  isOpen,
  onClose,
  instrument,
  duplicateFrom,
  mode,
  initialContext,
  classificationLocked = false,
  onSave,
  onError
}) => {
  const [formData, setFormData] = useState<InstrumentFormData>({
    symbol: '',
    displayName: '',
    assetClass: 'stocks',
    marketSegment: 'us-equities',
    subAssetClass: '',
    customMarketSegment: '',
    customSubAssetClass: '',
    contractSize: 1,
    contractAsset: '',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: '',
    status: 'active'
  });
  
  // Currency chooser modal state
  const { isOpen: isCurrencyChooserOpen, onOpen: onCurrencyChooserOpen, onClose: onCurrencyChooserClose } = useDisclosure();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof InstrumentFormData, string>>>({});
  
  // Template revert feature state
  const [showCustomizations, setShowCustomizations] = useState(false);
  const [templateData, setTemplateData] = useState<InstrumentCatalogEntry | null>(null);
  
  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string | React.ReactNode;
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  
  // Determine effective mode (instrument prop takes precedence over duplicateFrom)
  const effectiveMode: InstrumentModalMode = mode ?? (instrument ? 'edit' : 'add');
  const isEditMode = effectiveMode === 'edit';
  const isViewMode = effectiveMode === 'view';
  const isDuplicateMode = !!duplicateFrom && !instrument;

  // Modal title and subtitle based on mode
  const modalTitle = isViewMode
    ? 'View Instrument'
    : isDuplicateMode
      ? 'Duplicate Instrument'
      : isEditMode
        ? 'Edit Instrument'
        : 'Add Custom Instrument';

  const modalSubtitle = isViewMode
    ? 'Review instrument specifications'
    : isDuplicateMode
      ? 'Clone this instrument for a new contract'
      : isEditMode
        ? 'Update the instrument specifications below'
        : 'Create a custom instrument for your trading library';

  const submitButtonText = isEditMode ? 'Save Changes' : 'Add Instrument';
  
  // Get valid options based on selected asset class using exported constants
  const validMarketSegments = useMemo(() => {
    return VALID_MARKET_SEGMENTS_BY_ASSET_CLASS[formData.assetClass] || [];
  }, [formData.assetClass]);
  
  const validSubAssetClasses = useMemo(() => {
    return VALID_SUB_ASSET_CLASSES_BY_ASSET_CLASS[formData.assetClass] || [];
  }, [formData.assetClass]);

  // Template revert feature computed values
  const hasTemplateReference = useMemo(() => {
    return isEditMode && instrument?.instrumentReferenceId;
  }, [isEditMode, instrument?.instrumentReferenceId]);

  const customizedFieldsList = useMemo(() => {
    return instrument?.customizedFields || [];
  }, [instrument?.customizedFields]);

  const customizedFieldsCount = useMemo(() => {
    return customizedFieldsList.length;
  }, [customizedFieldsList]);

  const shouldShowRevertFeatures = useMemo(() => {
    return hasTemplateReference && customizedFieldsCount > 0;
  }, [hasTemplateReference, customizedFieldsCount]);
  
  // Pre-populate form for edit/view mode, duplicate mode, or with initial context for new instruments
  useEffect(() => {
    if (instrument) {
      // Edit/View mode: populate with existing instrument data
      setFormData({
        symbol: instrument.symbol,
        displayName: instrument.displayName,
        assetClass: instrument.classification.assetClass,
        marketSegment: instrument.classification.marketSegment,
        subAssetClass: instrument.classification.subAssetClass || '',
        customMarketSegment: instrument.classification.customMarketSegment || '',
        customSubAssetClass: instrument.classification.customSubAssetClass || '',
        contractSize: instrument.contractSize,
        contractAsset: instrument.contractAsset,
        incrementSize: instrument.incrementSize,
        incrementType: instrument.incrementType,
        quoteCurrency: instrument.quoteCurrency,
        notes: instrument.notes || '',
        status: instrument.status
      });
    } else if (duplicateFrom) {
      // Duplicate mode: pre-fill from source instrument (symbol unchanged, displayName gets " (Copy)" suffix)
      // Preserves instrumentReferenceId and customizedFields for rollover use case (handled in handleSubmit)
      setFormData({
        symbol: duplicateFrom.symbol,
        displayName: `${duplicateFrom.displayName} (Copy)`,
        assetClass: duplicateFrom.classification.assetClass,
        marketSegment: duplicateFrom.classification.marketSegment,
        subAssetClass: duplicateFrom.classification.subAssetClass || '',
        customMarketSegment: duplicateFrom.classification.customMarketSegment || '',
        customSubAssetClass: duplicateFrom.classification.customSubAssetClass || '',
        contractSize: duplicateFrom.contractSize,
        contractAsset: duplicateFrom.contractAsset,
        incrementSize: duplicateFrom.incrementSize,
        incrementType: duplicateFrom.incrementType,
        quoteCurrency: duplicateFrom.quoteCurrency,
        notes: duplicateFrom.notes || '',
        status: 'active' // New duplicates start as active
      });
    } else {
      // Add mode: use initial context if provided, otherwise use defaults
      const defaultAssetClass = initialContext?.assetClass || 'stocks';
      const validMarketSegments = VALID_MARKET_SEGMENTS_BY_ASSET_CLASS[defaultAssetClass] || [];
      const defaultMarketSegment = initialContext?.marketSegment || validMarketSegments[0] || 'us-equities';

      setFormData({
        symbol: '',
        displayName: '',
        assetClass: defaultAssetClass,
        marketSegment: defaultMarketSegment,
        subAssetClass: initialContext?.subAssetClass || '',
        customMarketSegment: '',
        customSubAssetClass: '',
        contractSize: 1,
        contractAsset: '',
        incrementSize: 0.01,
        incrementType: 'cent',
        quoteCurrency: 'USD',
        notes: '',
        status: 'active'
      });
    }
    setErrors({});
  }, [instrument, duplicateFrom, initialContext, isOpen]);

  // Load template data for revert functionality
  useEffect(() => {
    if (hasTemplateReference && instrument?.instrumentReferenceId) {
      const template = getInstrumentById(instrument.instrumentReferenceId);
      setTemplateData(template || null);
    } else {
      setTemplateData(null);
    }
  }, [hasTemplateReference, instrument?.instrumentReferenceId]);
  
  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof InstrumentFormData, string>> = {};
    
    if (!formData.symbol.trim()) {
      newErrors.symbol = 'Symbol is required';
    }
    
    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    }
    
    if (!formData.contractAsset.trim()) {
      newErrors.contractAsset = 'Contract asset is required';
    }
    
    if (!formData.quoteCurrency.trim()) {
      newErrors.quoteCurrency = 'Quote currency is required';
    }
    
    if (formData.contractSize <= 0) {
      newErrors.contractSize = 'Contract size must be greater than 0';
    }
    
    if (formData.incrementSize <= 0) {
      newErrors.incrementSize = 'Increment size must be greater than 0';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  /**
   * Handle form submission
   */
  const handleSubmit = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      let savedInstrument: Instrument;
      
      // Calculate price precision from increment size
      const calculatePricePrecision = (incrementSize: number): number => {
        const decimalPlaces = incrementSize.toString().split('.')[1]?.length || 0;
        return Math.max(0, decimalPlaces);
      };
      
      const pricePrecision = calculatePricePrecision(formData.incrementSize);
      
      if (isEditMode && instrument) {
        // Update existing instrument
        
        // Track which fields have been customized from the template
        const newCustomizedFields = [...(instrument.customizedFields || [])];
        
        // Only check for template changes if instrument has a reference template
        if (instrument.instrumentReferenceId) {
          const template = getInstrumentById(instrument.instrumentReferenceId);
          if (template) {
            // Define trackable fields that can be customized
            const trackableFields = [
              { key: 'symbol', currentValue: formData.symbol, templateValue: template.symbol },
              { key: 'displayName', currentValue: formData.displayName, templateValue: template.name },
              { key: 'assetClass', currentValue: formData.assetClass, templateValue: template.classification.assetClass },
              { key: 'subAssetClass', currentValue: formData.subAssetClass || '', templateValue: template.classification.subAssetClass || '' },
              { key: 'marketSegment', currentValue: formData.marketSegment || '', templateValue: template.classification.marketSegment || '' },
              { key: 'contractSize', currentValue: formData.contractSize, templateValue: template.contractSize },
              { key: 'contractAsset', currentValue: formData.contractAsset, templateValue: template.contractAsset },
              { key: 'incrementSize', currentValue: formData.incrementSize, templateValue: template.incrementSize },
              { key: 'incrementType', currentValue: formData.incrementType, templateValue: template.incrementType },
              { key: 'quoteCurrency', currentValue: formData.quoteCurrency, templateValue: template.quoteCurrency }
            ];
            
            // Check each field for customization
            for (const field of trackableFields) {
              const isModified = field.currentValue !== field.templateValue;
              const isAlreadyCustomized = newCustomizedFields.includes(field.key);
              
              if (isModified && !isAlreadyCustomized) {
                // Field was modified from template - mark as customized
                newCustomizedFields.push(field.key);
              } else if (!isModified && isAlreadyCustomized) {
                // Field was restored to template value - remove from customized
                const index = newCustomizedFields.indexOf(field.key);
                if (index > -1) {
                  newCustomizedFields.splice(index, 1);
                }
              }
            }
          }
        }

        const updatedInstrument: Instrument = {
          ...instrument,
          symbol: formData.symbol,
          displayName: formData.displayName,
          classification: {
            assetClass: formData.assetClass,
            marketSegment: formData.marketSegment,
            ...(formData.marketSegment === 'other' && { customMarketSegment: formData.customMarketSegment }),
            ...(formData.subAssetClass && { subAssetClass: formData.subAssetClass }),
            ...(formData.subAssetClass === 'other' && { customSubAssetClass: formData.customSubAssetClass })
          },
          contractSize: formData.contractSize,
          contractAsset: formData.contractAsset,
          incrementSize: formData.incrementSize,
          incrementType: formData.incrementType,
          quoteCurrency: formData.quoteCurrency,
          pricePrecision,
          notes: formData.notes,
          status: formData.status,
          customizedFields: newCustomizedFields,
          // Note: updatedAt is managed by BaseService.update(), don't set it here
        };
        savedInstrument = await instrumentService.update(instrument.id, updatedInstrument);
      } else {
        // Create new instrument (or duplicate from existing)

        // Track customized fields when duplicating from a template-based instrument
        let duplicateCustomizedFields: string[] | undefined;
        if (duplicateFrom?.instrumentReferenceId) {
          // Start with inherited customizations from source instrument
          const newCustomizedFields = [...(duplicateFrom.customizedFields || [])];

          // Load template to compare current form values
          const template = getInstrumentById(duplicateFrom.instrumentReferenceId);
          if (template) {
            // Define trackable fields (same pattern as edit mode)
            const trackableFields = [
              { key: 'symbol', currentValue: formData.symbol, templateValue: template.symbol },
              { key: 'displayName', currentValue: formData.displayName, templateValue: template.name },
              { key: 'assetClass', currentValue: formData.assetClass, templateValue: template.classification.assetClass },
              { key: 'subAssetClass', currentValue: formData.subAssetClass || '', templateValue: template.classification.subAssetClass || '' },
              { key: 'marketSegment', currentValue: formData.marketSegment || '', templateValue: template.classification.marketSegment || '' },
              { key: 'contractSize', currentValue: formData.contractSize, templateValue: template.contractSize },
              { key: 'contractAsset', currentValue: formData.contractAsset, templateValue: template.contractAsset },
              { key: 'incrementSize', currentValue: formData.incrementSize, templateValue: template.incrementSize },
              { key: 'incrementType', currentValue: formData.incrementType, templateValue: template.incrementType },
              { key: 'quoteCurrency', currentValue: formData.quoteCurrency, templateValue: template.quoteCurrency }
            ];

            // Check each field for customization (add if differs from template)
            for (const field of trackableFields) {
              const isModified = field.currentValue !== field.templateValue;
              const isAlreadyCustomized = newCustomizedFields.includes(field.key);

              if (isModified && !isAlreadyCustomized) {
                // Field differs from template - mark as customized
                newCustomizedFields.push(field.key);
              }
              // Note: Unlike edit mode, we don't remove fields that match template
              // because the inherited customization from source should be preserved
            }
          }

          duplicateCustomizedFields = newCustomizedFields.length > 0 ? newCustomizedFields : undefined;
        }

        const newInstrument: Omit<Instrument, 'id' | 'createdAt' | 'updatedAt'> = {
          symbol: formData.symbol,
          displayName: formData.displayName,
          classification: {
            assetClass: formData.assetClass,
            marketSegment: formData.marketSegment,
            ...(formData.marketSegment === 'other' && { customMarketSegment: formData.customMarketSegment }),
            ...(formData.subAssetClass && { subAssetClass: formData.subAssetClass }),
            ...(formData.subAssetClass === 'other' && { customSubAssetClass: formData.customSubAssetClass })
          },
          contractSize: formData.contractSize,
          contractAsset: formData.contractAsset,
          incrementSize: formData.incrementSize,
          incrementType: formData.incrementType,
          quoteCurrency: formData.quoteCurrency,
          pricePrecision,
          notes: formData.notes,
          status: formData.status,
          // Preserve reference link when duplicating (for contract rollover use case)
          ...(duplicateFrom?.instrumentReferenceId && { instrumentReferenceId: duplicateFrom.instrumentReferenceId }),
          // Use computed customizedFields that includes any new customizations made during duplication
          ...(duplicateCustomizedFields && { customizedFields: duplicateCustomizedFields }),
        };
        savedInstrument = await instrumentService.add(newInstrument);
      }
      
      onSave(savedInstrument);
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred while saving';
      if (onError) {
        onError(errorMessage);
      }
      console.error('Error saving instrument:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Reset individual field to template value
   */
  const resetFieldToTemplate = (fieldName: string): void => {
    if (!templateData || !instrument) return;

    const newCustomizedFields = [...(instrument.customizedFields || [])];
    const fieldIndex = newCustomizedFields.indexOf(fieldName);
    if (fieldIndex > -1) {
      newCustomizedFields.splice(fieldIndex, 1);
    }

    // Update form field with template value
    switch (fieldName) {
      case 'symbol':
        updateField('symbol', templateData.symbol);
        break;
      case 'displayName':
        updateField('displayName', templateData.name);
        break;
      case 'assetClass':
        updateField('assetClass', templateData.classification.assetClass);
        break;
      case 'subAssetClass':
        updateField('subAssetClass', templateData.classification.subAssetClass || '');
        break;
      case 'marketSegment':
        updateField('marketSegment', templateData.classification.marketSegment || '');
        break;
      case 'contractSize':
        updateField('contractSize', templateData.contractSize);
        break;
      case 'contractAsset':
        updateField('contractAsset', templateData.contractAsset);
        break;
      case 'incrementSize':
        updateField('incrementSize', templateData.incrementSize);
        break;
      case 'incrementType':
        updateField('incrementType', templateData.incrementType);
        break;
      case 'quoteCurrency':
        updateField('quoteCurrency', templateData.quoteCurrency);
        break;
    }

    // Update instrument's customizedFields immediately for UI reactivity
    if (instrument) {
      instrument.customizedFields = newCustomizedFields;
    }
  };

  /**
   * Reset all fields to template defaults
   */
  const resetAllFieldsToTemplate = (): void => {
    if (!templateData || !instrument) return;

    // Show confirmation dialog
    setConfirmationModal({
      isOpen: true,
      title: 'Reset All Fields',
      message: (
        <>
          Reset <span className="text-danger-600 font-bold">all customized fields</span> to template defaults? This action cannot be undone.
        </>
      ),
      onConfirm: () => {
        // Close modal first
        setConfirmationModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        
        // Execute the reset
        performResetAllFields();
      }
    });
  };

  const performResetAllFields = () => {
    if (!templateData || !instrument) return;

    // Update all trackable fields
    setFormData(prev => ({
      ...prev,
      symbol: templateData.symbol,
      displayName: templateData.name,
      assetClass: templateData.classification.assetClass,
      subAssetClass: templateData.classification.subAssetClass || '',
      marketSegment: templateData.classification.marketSegment || '',
      contractSize: templateData.contractSize,
      contractAsset: templateData.contractAsset,
      incrementSize: templateData.incrementSize,
      incrementType: templateData.incrementType,
      quoteCurrency: templateData.quoteCurrency
    }));

    // Clear customizedFields array
    if (instrument) {
      instrument.customizedFields = [];
    }
  };
  
  /**
   * Update form field value
   */
  const updateField = <K extends keyof InstrumentFormData>(
    field: K,
    value: InstrumentFormData[K]
  ): void => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  /**
   * Get template value for a specific field
   */
  const getTemplateValue = (fieldName: string): string => {
    if (!templateData) return '';
    
    switch (fieldName) {
      case 'symbol':
        return templateData.symbol;
      case 'displayName':
        return templateData.name;
      case 'assetClass':
        return templateData.classification.assetClass;
      case 'subAssetClass':
        return templateData.classification.subAssetClass || '';
      case 'marketSegment':
        return templateData.classification.marketSegment || '';
      case 'contractSize':
        return templateData.contractSize.toString();
      case 'contractAsset':
        return templateData.contractAsset;
      case 'incrementSize':
        return templateData.incrementSize.toString();
      case 'incrementType':
        return templateData.incrementType;
      case 'quoteCurrency':
        return templateData.quoteCurrency;
      default:
        return '';
    }
  };

  /**
   * Check if a field is customized
   */
  const isFieldCustomized = (fieldName: string): boolean => {
    return customizedFieldsList.includes(fieldName);
  };

  /**
   * Render field with customization indicators
   */
  const renderCustomizedField = (fieldName: string, children: React.ReactNode): React.ReactNode => {
    const isCustomized = isFieldCustomized(fieldName);
    const templateValue = getTemplateValue(fieldName);
    
    if (!showCustomizations || !isCustomized) {
      return children;
    }

    return (
      <div className="space-y-1">
        <div className="relative">
          <div className="border border-secondary-200 rounded-lg p-0">
            {children}
          </div>
          <div className="absolute -top-2 -right-2 flex items-center gap-1">
            <span className="text-secondary-600">🔧</span>
            <Button
              size="sm"
              color="secondary"
              variant="ghost"
              onPress={() => {
                setConfirmationModal({
                  isOpen: true,
                  title: 'Reset Field',
                  message: (
                    <>
                      Reset <span className="text-secondary font-bold">{fieldName}</span> to template value?
                    </>
                  ),
                  onConfirm: () => {
                    setConfirmationModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
                    resetFieldToTemplate(fieldName);
                  }
                });
              }}
              className="h-6 min-w-6 px-1"
            >
              ↻
            </Button>
          </div>
        </div>
        {templateValue && (
          <div className="text-xs text-secondary-600 ml-2">
            ↳ Template value: "{templateValue}"
          </div>
        )}
      </div>
    );
  };
  
  return (
    <Modal 
      isOpen={isOpen} 
      onOpenChange={(open) => !open && onClose()}
      size="2xl"
      placement="center"
      scrollBehavior="inside"
      shouldBlockScroll={false}
      disableAnimation
      classNames={{ 
        wrapper: "items-start h-auto", 
        base: "my-auto" 
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold">{modalTitle}</h2>
              <p className="text-small text-foreground-500">{modalSubtitle}</p>
            </div>
            {shouldShowRevertFeatures && !isViewMode && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-600">🔍 Show Customizations</span>
                <Switch
                  isSelected={showCustomizations}
                  onValueChange={setShowCustomizations}
                  size="sm"
                />
              </div>
            )}
          </div>
        </ModalHeader>
        
        <ModalBody>
          <div className="space-y-6">
            {/* Section 1: General Information */}
            <div className="space-y-4">
              <div className="border-b border-divider pb-2">
                <h3 className="text-lg font-medium text-foreground">General Information</h3>
                <p className="text-sm text-foreground-500">Basic instrument identification and classification</p>
              </div>
              
              {/* Symbol and Display Name Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderCustomizedField('symbol',
                  <Input
                    label="Symbol"
                    placeholder="e.g., AAPL, ES, EUR/USD"
                    value={formData.symbol}
                    onChange={(e) => updateField('symbol', e.target.value)}
                    isInvalid={!!errors.symbol}
                    errorMessage={errors.symbol}
                    isRequired={!isViewMode}
                    isReadOnly={isViewMode}
                  />
                )}

                {renderCustomizedField('displayName',
                  <Input
                    label="Display Name"
                    placeholder="e.g., Apple Inc., E-mini S&P 500"
                    value={formData.displayName}
                    onChange={(e) => updateField('displayName', e.target.value)}
                    isInvalid={!!errors.displayName}
                    errorMessage={errors.displayName}
                    isRequired={!isViewMode}
                    isReadOnly={isViewMode}
                  />
                )}
              </div>
              
              {/* Asset Classification */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {renderCustomizedField('assetClass',
                  <Select
                    label="Asset Class"
                    placeholder="Select asset class"
                    selectedKeys={[formData.assetClass]}
                    onSelectionChange={(keys) => {
                      const value = Array.from(keys)[0] as string;
                      updateField('assetClass', value as AssetClassType);
                      // Reset dependent fields when asset class changes
                      updateField('marketSegment', validMarketSegments[0] || 'other');
                      updateField('subAssetClass', '');
                      updateField('customMarketSegment', '');
                      updateField('customSubAssetClass', '');
                    }}
                    isRequired={!isViewMode}
                    isDisabled={isViewMode || classificationLocked}
                  >
                    {assetClassOptions.map((assetClass) => (
                      <SelectItem key={assetClass}>
                        {assetClass.charAt(0).toUpperCase() + assetClass.slice(1)}
                      </SelectItem>
                    ))}
                  </Select>
                )}

                {renderCustomizedField('marketSegment',
                  <Select
                    label="Market Segment"
                    placeholder="Select market segment"
                    selectedKeys={[formData.marketSegment]}
                    onSelectionChange={(keys) => {
                      const value = Array.from(keys)[0] as string;
                      updateField('marketSegment', value as MarketSegment);
                      // Clear custom field if not selecting 'other'
                      if (value !== 'other') {
                        updateField('customMarketSegment', '');
                      }
                    }}
                    isRequired={!isViewMode}
                    isDisabled={isViewMode || classificationLocked}
                  >
                    {validMarketSegments.map((segment) => (
                      <SelectItem key={segment}>
                        {segment === 'other' ? 'Other' : segment.split('-').map(word =>
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ')}
                      </SelectItem>
                    ))}
                  </Select>
                )}

                {renderCustomizedField('subAssetClass',
                  <Select
                    label="Sub-Asset Class"
                    placeholder="Optional sub-class"
                    selectedKeys={formData.subAssetClass ? [formData.subAssetClass] : []}
                    onSelectionChange={(keys) => {
                      const value = Array.from(keys)[0] as string;
                      updateField('subAssetClass', value as SubAssetClass);
                      // Clear custom field if not selecting 'other'
                      if (value !== 'other') {
                        updateField('customSubAssetClass', '');
                      }
                    }}
                    isDisabled={isViewMode || classificationLocked}
                  >
                    {validSubAssetClasses.map((subClass) => (
                      <SelectItem key={subClass}>
                        {subClass === 'other' ? 'Other' : subClass.split('-').map(word =>
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ')}
                      </SelectItem>
                    ))}
                  </Select>
                )}
              </div>
              
              {/* Custom fields for 'other' selections */}
              {formData.marketSegment === 'other' && (
                <Input
                  label="Custom Market Segment"
                  placeholder="Describe your custom market segment"
                  value={formData.customMarketSegment}
                  onChange={(e) => updateField('customMarketSegment', e.target.value)}
                  isRequired={!isViewMode}
                  isReadOnly={isViewMode || classificationLocked}
                />
              )}

              {formData.subAssetClass === 'other' && (
                <Input
                  label="Custom Sub-Asset Class"
                  placeholder="Describe your custom sub-asset class"
                  value={formData.customSubAssetClass}
                  onChange={(e) => updateField('customSubAssetClass', e.target.value)}
                  isRequired={!isViewMode}
                  isReadOnly={isViewMode || classificationLocked}
                />
              )}

              {/* Notes */}
              <TextArea
                label="Notes"
                placeholder="Optional notes about this instrument..."
                value={formData.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                minRows={2}
                isReadOnly={isViewMode}
              />
            </div>
            
            {/* Section 2: Contract Specifications */}
            <div className="space-y-4">
              <div className="border-b border-divider pb-2">
                <h3 className="text-lg font-medium text-foreground">Contract Specifications</h3>
                <p className="text-sm text-foreground-500">Trading mechanics and value calculations</p>
              </div>
              
              {/* Contract Definition */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderCustomizedField('contractSize',
                  <NumberInput
                    label="Contract Size"
                    placeholder="e.g., 100, 50"
                    description="Number of units per contract (e.g., 100 shares for stocks, 50 for ES futures)"
                    value={formData.contractSize}
                    onValueChange={(value) => updateField('contractSize', value || 1)}
                    isInvalid={!!errors.contractSize}
                    errorMessage={errors.contractSize}
                    min={0.01}
                    step={1}
                    isRequired={!isViewMode}
                    isDisabled={isViewMode}
                  />
                )}

                {renderCustomizedField('contractAsset',
                  <Input
                    label="Contract Unit"
                    placeholder="e.g., shares, troy oz, barrels, EUR"
                    description="What each contract represents (e.g., 'shares', 'troy oz', 'barrels', 'EUR')"
                    value={formData.contractAsset}
                    onChange={(e) => updateField('contractAsset', e.target.value)}
                    isInvalid={!!errors.contractAsset}
                    errorMessage={errors.contractAsset}
                    isRequired={!isViewMode}
                    isReadOnly={isViewMode}
                  />
                )}
              </div>

              {/* Quote Currency */}
              {renderCustomizedField('quoteCurrency',
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-foreground">
                    Quote Currency {!isViewMode && '*'}
                  </label>
                  <Button
                    variant="bordered"
                    className={`w-full justify-between h-14 ${
                      errors.quoteCurrency ? 'border-danger' : ''
                    }`}
                    onClick={onCurrencyChooserOpen}
                    isDisabled={isViewMode}
                  >
                    <div className="flex items-center gap-3">
                      {formData.quoteCurrency ? (
                        <>
                          <div className="w-8 h-8 rounded-full bg-content2 flex items-center justify-center font-bold text-xs">
                            {getCurrencySymbol(formData.quoteCurrency, CURRENCY_DATABASE) || formData.quoteCurrency.slice(0, 2)}
                          </div>
                          <div className="text-left">
                            <div className="font-semibold">{formData.quoteCurrency}</div>
                            <div className="text-sm text-text-secondary">
                              {getCurrencyDisplayName(formData.quoteCurrency, CURRENCY_DATABASE)}
                            </div>
                          </div>
                        </>
                      ) : (
                        <span className="text-text-secondary">Select currency</span>
                      )}
                    </div>
                    {!isViewMode && <div className="text-text-secondary">🔍</div>}
                  </Button>
                  <div className="text-sm text-text-secondary">
                    Currency used for P&L calculations and price quotes
                  </div>
                  {errors.quoteCurrency && (
                    <div className="text-sm text-danger">
                      {errors.quoteCurrency}
                    </div>
                  )}
                </div>
              )}
              
              {/* Increment Specifications */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderCustomizedField('incrementSize',
                  <NumberInput
                    label="Increment Size"
                    placeholder="e.g., 0.25, 0.00001"
                    description="Minimum price movement in quote currency (e.g., 0.25 for ES, 0.00001 for forex)"
                    value={formData.incrementSize}
                    onValueChange={(value) => updateField('incrementSize', value || 0.01)}
                    isInvalid={!!errors.incrementSize}
                    errorMessage={errors.incrementSize}
                    min={0.00001}
                    step={0.00001}
                    formatOptions={{
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 8
                    }}
                    isRequired={!isViewMode}
                    isDisabled={isViewMode}
                  />
                )}

                {renderCustomizedField('incrementType',
                  <Select
                    label="Increment Type"
                    placeholder="Select increment type"
                    description="Market terminology for price movement (tick for futures, pip for forex)"
                    selectedKeys={[formData.incrementType]}
                    onSelectionChange={(keys) => {
                      const value = Array.from(keys)[0] as string;
                      updateField('incrementType', value as IncrementType);
                    }}
                    isRequired={!isViewMode}
                    isDisabled={isViewMode}
                  >
                    {incrementTypeOptions.map((incrementType) => (
                      <SelectItem key={incrementType}>
                        {incrementType.charAt(0).toUpperCase() + incrementType.slice(1)}
                      </SelectItem>
                    ))}
                  </Select>
                )}
              </div>
              
              {/* Contract Value Display */}
              <div className="p-4 bg-default-50 rounded-lg border border-default-200">
                <h4 className="text-sm font-medium text-foreground mb-2">Contract Specification Summary</h4>
                <div className="text-sm text-foreground-600 space-y-1">
                  <p>
                    <strong>Contract Definition:</strong> One contract represents {formData.contractSize} {formData.contractAsset}
                  </p>
                  <p>
                    <strong>Increment Value:</strong> 1 {formData.incrementType} = {formData.quoteCurrency} {formData.incrementSize}
                  </p>
                  <p>
                    <strong>Contract Value per {formData.incrementType}:</strong> {formData.quoteCurrency} {(formData.contractSize * formData.incrementSize).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Section 3: System Status */}
            <Accordion>
              <AccordionItem 
                key="system-status" 
                aria-label="System Status" 
                title={
                  <div className="flex items-center gap-2">
                    <span>System Status</span>
                    {shouldShowRevertFeatures && (
                      <Chip size="sm" color="warning" variant="flat" className="flex items-center gap-1">
                        🔧 {customizedFieldsCount}
                      </Chip>
                    )}
                  </div>
                }
                subtitle="Metadata and system information"
              >
                <div className="space-y-4 pt-2">
                  {/* Status (Editable in add/edit mode, read-only in view mode) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Select
                      label="Status"
                      placeholder="Select status"
                      selectedKeys={[formData.status]}
                      onSelectionChange={(keys) => {
                        const value = Array.from(keys)[0] as string;
                        updateField('status', value as EntityStatus);
                      }}
                      isRequired={!isViewMode}
                      isDisabled={isViewMode}
                    >
                      <SelectItem key="active">
                        Active
                      </SelectItem>
                      <SelectItem key="inactive">
                        Inactive
                      </SelectItem>
                      <SelectItem key="archived">
                        Archived
                      </SelectItem>
                    </Select>
                    
                    {/* Price Precision (Read-only, calculated) */}
                    <Input
                      label="Price Precision"
                      value={`${Math.max(0, formData.incrementSize.toString().split('.')[1]?.length || 0)} decimal places`}
                      isReadOnly
                      variant="flat"
                      color="default"
                    />
                  </div>
                  
                  {/* System Information (Read-only) */}
                  {isEditMode && instrument && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="Created"
                        value={new Date(instrument.createdAt).toLocaleString()}
                        isReadOnly
                        variant="flat"
                        color="default"
                      />
                      
                      <Input
                        label="Last Updated"
                        value={new Date(instrument.updatedAt).toLocaleString()}
                        isReadOnly
                        variant="flat"
                        color="default"
                      />
                    </div>
                  )}
                  
                  {/* Reference Template Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Source Type"
                      value={isEditMode && instrument?.instrumentReferenceId ? "From Reference Catalog" : "Custom Instrument"}
                      isReadOnly
                      variant="flat"
                      color="default"
                    />
                    
                    {isEditMode && instrument?.instrumentReferenceId && (
                      <Input
                        label="Template ID"
                        value={instrument.instrumentReferenceId}
                        isReadOnly
                        variant="flat"
                        color="default"
                      />
                    )}
                  </div>
                  
                  {/* Template Customization Info */}
                  {shouldShowRevertFeatures && templateData && (
                    <div className="space-y-3">
                      <div className="p-3 bg-warning-50 border border-warning-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-warning-600">🔧</span>
                          <span className="text-sm font-medium text-warning-700">
                            {customizedFieldsCount} field{customizedFieldsCount !== 1 ? 's' : ''} customized from template
                          </span>
                        </div>
                        <div className="text-xs text-warning-600 mb-3">
                          Template: {templateData.name} ({templateData.symbol})
                        </div>
                        <Button
                          size="sm"
                          color="warning"
                          variant="ghost"
                          onPress={resetAllFieldsToTemplate}
                          className="w-full"
                        >
                          🔄 Reset All to Template Defaults
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </AccordionItem>
            </Accordion>
          </div>
        </ModalBody>
        
        <ModalFooter>
          <Button
            color="default"
            variant="light"
            onPress={onClose}
            isDisabled={isSubmitting}
          >
            {isViewMode ? 'Close' : 'Cancel'}
          </Button>

          {!isViewMode && (
            <Button
              color="primary"
              onPress={handleSubmit}
              isLoading={isSubmitting}
            >
              {isSubmitting ? (isEditMode ? 'Saving...' : 'Adding...') : submitButtonText}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
      
      {/* Currency Chooser Modal */}
      <CurrencyChooserModal
        isOpen={isCurrencyChooserOpen}
        onClose={onCurrencyChooserClose}
        selectedCurrency={formData.quoteCurrency}
        onCurrencySelect={(currencyCode) => {
          updateField('quoteCurrency', currencyCode);
        }}
        title="Choose Quote Currency"
      />
      
      {/* Confirmation Modal */}
      <Modal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
        size="sm"
        placement="center"
        backdrop="opaque"
        className='border border-default-500'
      >
        <ModalContent>
          <ModalHeader className="border-b border-default-200 bg-default-100">
            {/* <h3 className="text-lg font-semibold">{confirmationModal.title}</h3> */}
            <p className='text-text-secondary'>
              {confirmationModal.title}
            </p>
          </ModalHeader>
          <ModalBody className='items-center bg-default-50'>
            <p className="text-text-primary mt-4 mb-4 ">
              {confirmationModal.message}
            </p>
          </ModalBody>
          <ModalFooter className= "border-t border-default-200 bg-default-100">
            <Button 
              color="default" 
              variant="light"
              size="sm" 
              onPress={() => setConfirmationModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
            >
              Cancel
            </Button>
            <Button 
              color="primary" 
              onPress={confirmationModal.onConfirm}
              variant="ghost"
              size="sm" 
            >
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Modal>
  );
};